import { AbsenceCategory, RescheduleCause } from "@myapp/contract";
import { getAbsenceCategoryLabel } from "src/entities/absence";
import { NEEDS_RESCHEDULING_LABEL } from "src/entities/task";

/**
 * Why a block is flagged, in the dispatcher's words.
 *
 * `ABSENCE` is deliberately absent: that cause carries an `AbsenceCategory` in
 * `reschedulingReason` and is labelled with it ("Krankheit", "Urlaub"), which is more specific
 * than the cause itself would be.
 */
const CAUSE_LABELS: Record<Exclude<RescheduleCause, "ABSENCE">, string> = {
  // Same wording as the WorkingHoursDialog the dispatcher clicked through to get here.
  OUTSIDE_WORKING_HOURS: "Außerhalb der Arbeitszeit",
  TECHNICIAN_ARCHIVED: "Techniker archiviert",
  NOT_CLOSED: "Nicht abgeschlossen",
};

/** `NOT_CLOSED` on an item nobody tapped: nothing to close, it was never started. */
const NOT_STARTED_LABEL = "Nicht gestartet";

const ABSENCE_CATEGORIES = new Set<string>([
  "SICK_LEAVE",
  "VACATION",
  "TRAINING",
  "PERSONAL_LEAVE",
]);

/**
 * The "why" on its own — `Außerhalb der Arbeitszeit`, `Krankheit` — or null when the row predates
 * `reschedulingCause` and carries no reason either.
 *
 * `reason` is typed loosely because `RescheduleItem` flattens it to a plain string; anything that
 * is not a known absence category is passed through rather than dropped.
 *
 * `started` only matters for `NOT_CLOSED`: one stored cause, two wordings — „Nicht gestartet" when
 * no Anfahrt/Arbeit was ever tapped, „Nicht abgeschlossen" otherwise. Left out, it reads as the
 * latter.
 */
export function getReschedulingDetail(
  cause: RescheduleCause | null | undefined,
  reason: string | null | undefined,
  started?: boolean,
): string | null {
  if (cause === "NOT_CLOSED" && started === false) return NOT_STARTED_LABEL;
  if (cause && cause !== "ABSENCE") return CAUSE_LABELS[cause];
  if (!reason) return null;

  return ABSENCE_CATEGORIES.has(reason)
    ? getAbsenceCategoryLabel(reason as AbsenceCategory)
    : reason;
}

/**
 * `Braucht Umdisponierung: Krankheit`. One wording for the whole app — the disposition board, the
 * Umdisponierung list and the work-order detail page all read from here.
 *
 * Before `reschedulingCause` existed the suffix came from `reschedulingReason` alone, which only
 * ever answered "which absence displaced it". A block force-planned onto a Saturday has no absence
 * behind it and used to render as a bare "Braucht Umdisponierung" with no clue why.
 */
export function buildReschedulingLabel(
  cause: RescheduleCause | null | undefined,
  reason: string | null | undefined,
  started?: boolean,
): string {
  const detail = getReschedulingDetail(cause, reason, started);
  return detail ? `${NEEDS_RESCHEDULING_LABEL}: ${detail}` : NEEDS_RESCHEDULING_LABEL;
}
